/**
 * Onboarding wizard del tenant — 4 pasos guiados que el owner sigue al crear su tienda.
 *
 * Estados (`tenant.metadata.onboardingState`):
 *   pending        → no empezó
 *   step-info      → confirmó datos básicos / branding
 *   step-product   → creó al menos un producto
 *   step-payment   → configuró un payment method
 *   completed      → onboarding cerrado, banner desaparece
 *
 * Cada CTA marca el avance con PATCH `/api/admin/tenants/:id/onboarding`. El usuario
 * puede saltar pasos (no son obligatorios), solo guía. Marcar 'completed' lo cierra.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, Button } from '@/components/ui'
import { adminApi } from '@/lib/api'
import { Check, ArrowRight, Package, CreditCard, Building2, Sparkles, Rocket } from 'lucide-react'
import { cn } from '@/lib/cn'

export const Route = createFileRoute('/store/$id/onboarding')({
  component: OnboardingPage,
})

type OnboardingState = 'pending' | 'step-info' | 'step-product' | 'step-payment' | 'completed'

interface Tenant {
  id: string
  name: string
  metadata: Record<string, string>
}

const STEPS: Array<{
  key: OnboardingState
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  cta: string
  href: string
}> = [
  {
    key: 'step-info',
    label: 'Datos de la tienda',
    description: 'Confirmá nombre, dominio y branding (logo, color principal).',
    icon: Building2,
    cta: 'Ir a ajustes',
    href: '/store/$id/settings',
  },
  {
    key: 'step-product',
    label: 'Tu primer producto',
    description: 'Subí imágenes, completá precio y descripción. Puede ser un draft.',
    icon: Package,
    cta: 'Crear producto',
    href: '/store/$id/products',
  },
  {
    key: 'step-payment',
    label: 'Método de pago',
    description: 'Conectá tu pasarela (Bold, Wompi) o activá el provider Demo para probar.',
    icon: CreditCard,
    cta: 'Configurar pagos',
    href: '/store/$id/payments',
  },
]

const ORDER: OnboardingState[] = ['pending', 'step-info', 'step-product', 'step-payment', 'completed']

function OnboardingPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data } = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => adminApi.get<{ data: Tenant }>(`/api/admin/tenants/${id}`),
  })

  const advance = useMutation({
    mutationFn: (state: OnboardingState) =>
      adminApi.patch(`/api/admin/tenants/${id}/onboarding`, { state }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', id] }),
  })

  const tenant = data?.data
  const current = (tenant?.metadata?.onboardingState as OnboardingState | undefined) ?? 'pending'
  const currentIdx = ORDER.indexOf(current)

  function isDone(stepKey: OnboardingState) {
    return currentIdx > ORDER.indexOf(stepKey)
  }

  function markStep(stepKey: OnboardingState) {
    advance.mutate(stepKey)
  }

  function complete() {
    advance.mutate('completed', {
      onSuccess: () => navigate({ to: '/store/$id', params: { id } }),
    })
  }

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="size-5 text-blue-700 dark:text-blue-400" />
        <span className="text-xs font-mono uppercase tracking-widest text-blue-700 dark:text-blue-400">Onboarding</span>
      </div>
      <h1 className="text-3xl font-bold tracking-tight">Bienvenido{tenant ? ` a ${tenant.name}` : ''}</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-xl">
        Tres pasos rápidos para dejar tu tienda lista para vender. Podés saltarlos y completarlos
        después desde los menús — esto es solo una guía.
      </p>

      <div className="mt-8 space-y-3">
        {STEPS.map((step, idx) => {
          const done = isDone(step.key)
          const Icon = step.icon
          return (
            <Card
              key={step.key}
              className={cn(
                'p-5 transition',
                done ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/50' : 'hover:border-border',
              )}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'size-10 rounded-full flex items-center justify-center shrink-0 border-2',
                    done
                      ? 'bg-emerald-50 dark:bg-emerald-900/40 border-emerald-500 text-emerald-700 dark:text-emerald-400'
                      : 'bg-[hsl(var(--surface))] border-border text-muted-foreground',
                  )}
                >
                  {done ? <Check className="size-5" /> : <Icon className="size-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
                    Paso {idx + 1}
                  </p>
                  <h3 className="font-semibold">{step.label}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Link to={step.href} params={{ id }}>
                    <Button variant={done ? 'outline' : 'default'} size="sm">
                      {step.cta} <ArrowRight className="size-3.5 ml-1" />
                    </Button>
                  </Link>
                  {!done && (
                    <button
                      onClick={() => markStep(step.key)}
                      disabled={advance.isPending}
                      className="text-xs text-muted-foreground hover:text-foreground transition"
                    >
                      Marcar listo
                    </button>
                  )}
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      <div className="mt-10 border-t border-border pt-6 flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground">
          ¿Listo para empezar? Cerrá el onboarding y vamos al dashboard.
        </p>
        <Button onClick={complete} disabled={advance.isPending}>
          <Rocket className="size-4 mr-2" /> Finalizar y entrar
        </Button>
      </div>

      {advance.error && (
        <p className="mt-4 text-xs text-red-700 dark:text-red-400">{(advance.error as Error).message}</p>
      )}
    </div>
  )
}
